// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/PdfCitationPreview.test.tsx
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Flex, IconButton, Spinner, Text } from "@radix-ui/themes";
import { getDocument, type RenderTask } from "pdfjs-dist";
import { useCallback, useEffect, useRef, useState } from "react";

import { ensurePdfjsWorker } from "../../../../../utils/pdfjsWorker";

ensurePdfjsWorker();

type PdfCitationPreviewProps = {
  /** Raw bytes of the source PDF (from the per-source byte cache). */
  data: ArrayBuffer;
  /** 1-based page the citation points at — the viewer opens here, not page 1. */
  pageNumber: number;
  /** Normalized (0-1, top-left origin) pin position on the cited page. */
  relX?: number;
  relY?: number;
  /** Normalized (0-1) bounding box of the cited text — rendered as a
   * highlight rectangle instead of the center pin when present. */
  bbox?: { x: number; y: number; w: number; h: number } | null;
  /** Testid namespace — override when two previews can coexist on the page
   * (e.g. the inline rule-detail preview under an open viewer dialog), since
   * duplicated testids are Playwright strict-mode violations. */
  testIdPrefix?: string;
};

/**
 * Minimal pdf.js canvas viewer for rule citations, modeled on
 * FilePreviewBlock's cancel-safe render loop: one page at a time, the
 * in-flight RenderTask is cancelled on page change (pdf.js cannot render two
 * pages into one canvas), and a generation counter lets superseded renders
 * no-op. The citation pin renders only on the cited page.
 */
export function PdfCitationPreview({
  data,
  pageNumber,
  relX,
  relY,
  bbox,
  testIdPrefix = "knowledge-source-viewer",
}: PdfCitationPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentPage, setCurrentPage] = useState(pageNumber);
  const [totalPages, setTotalPages] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pdfDocRef = useRef<Awaited<
    ReturnType<typeof getDocument>["promise"]
  > | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const renderGenRef = useRef(0);

  const renderPage = useCallback(async (pageNum: number) => {
    const pdfDoc = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdfDoc || !canvas) return;

    const myGen = ++renderGenRef.current;
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch {
        // cancel() may throw if the task already settled; safe to ignore.
      }
      renderTaskRef.current = null;
    }

    setRendering(true);
    try {
      const page = await pdfDoc.getPage(pageNum);
      if (myGen !== renderGenRef.current) return;

      const viewport = page.getViewport({ scale: 1.25 });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const context = canvas.getContext("2d");
      if (!context) {
        setError("Failed to render page");
        setRendering(false);
        return;
      }

      const task = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = task;
      await task.promise;
      if (myGen === renderGenRef.current) {
        renderTaskRef.current = null;
        setRendering(false);
      }
    } catch (err) {
      if (
        (err as { name?: string } | null)?.name ===
        "RenderingCancelledException"
      ) {
        return;
      }
      if (myGen === renderGenRef.current) {
        setError("Failed to render page");
        setRendering(false);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setError(null);
      setRendering(true);
      pdfDocRef.current = null;
      try {
        // pdf.js TRANSFERS the buffer it is given to its worker (neutering
        // the original), so it must get a private copy — handing over the
        // shared cached buffer would detach it and crash the next viewer.
        const pdfDoc = await getDocument({
          data: new Uint8Array(data.slice(0)),
        }).promise;
        if (cancelled) return;
        pdfDocRef.current = pdfDoc;
        setTotalPages(pdfDoc.numPages);
        const startPage = Math.min(Math.max(1, pageNumber), pdfDoc.numPages);
        setCurrentPage(startPage);
        await renderPage(startPage);
      } catch {
        if (cancelled) return;
        setError("Failed to load PDF");
        setRendering(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      renderGenRef.current++;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // ignore
        }
        renderTaskRef.current = null;
      }
    };
  }, [data, pageNumber, renderPage]);

  function goToPage(next: number) {
    if (next < 1 || next > totalPages) return;
    setCurrentPage(next);
    renderPage(next);
  }

  const onCitedPage = currentPage === pageNumber && !rendering;
  const showBbox = onCitedPage && bbox != null;
  // The bbox rectangle subsumes the center pin — show the pin only when the
  // citation anchored to a point without a box.
  const showPin = onCitedPage && !showBbox && relX != null && relY != null;

  if (error) {
    return (
      <Text size="2" color="red" data-testid={`${testIdPrefix}-error`}>
        {error}
      </Text>
    );
  }

  return (
    <Flex direction="column" gap="2" data-testid={`${testIdPrefix}-pdf`}>
      <Flex gap="2" align="center" justify="center">
        <IconButton
          size="1"
          variant="soft"
          color="gray"
          disabled={currentPage <= 1}
          onClick={() => goToPage(currentPage - 1)}
          aria-label="Previous page"
          data-testid={`${testIdPrefix}-prev`}
        >
          <CaretLeft aria-hidden="true" />
        </IconButton>
        <Text size="1" data-testid={`${testIdPrefix}-page-indicator`}>
          Page {currentPage}
          {totalPages ? ` of ${totalPages}` : ""}
        </Text>
        <IconButton
          size="1"
          variant="soft"
          color="gray"
          disabled={totalPages === 0 || currentPage >= totalPages}
          onClick={() => goToPage(currentPage + 1)}
          aria-label="Next page"
          data-testid={`${testIdPrefix}-next`}
        >
          <CaretRight aria-hidden="true" />
        </IconButton>
      </Flex>
      <div className="max-h-[70vh] overflow-auto">
        {rendering ? (
          <Flex justify="center" className="py-8">
            <Spinner />
          </Flex>
        ) : null}
        {/* inline-block wrapper hugs the canvas box so percentage overlays
            track the rendered page, not the (possibly wider) scroll area. */}
        <div className="relative inline-block">
          <canvas
            ref={canvasRef}
            className="max-w-full"
            aria-label="Cited document page"
            data-testid={`${testIdPrefix}-canvas`}
          />
          {showBbox ? (
            <span
              aria-label="Citation highlight"
              className="border-blue-9 pointer-events-none absolute block rounded-sm border-2 border-solid shadow-md"
              style={{
                left: `${bbox.x * 100}%`,
                top: `${bbox.y * 100}%`,
                width: `${bbox.w * 100}%`,
                height: `${bbox.h * 100}%`,
              }}
              data-testid={`${testIdPrefix}-bbox`}
            />
          ) : null}
          {showPin ? (
            <span
              aria-label="Citation location"
              className="border-white bg-blue-9 absolute block size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-solid shadow-md"
              style={{ left: `${relX * 100}%`, top: `${relY * 100}%` }}
              data-testid={`${testIdPrefix}-pin`}
            />
          ) : null}
        </div>
      </div>
    </Flex>
  );
}
